import * as React from "react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusPill,
  Textarea,
  toast,
  type StatusPillStatus,
} from "@eduviaos/ui-kit";
import {
  AlertCircle,
  CalendarPlus,
  Copy,
  ExternalLink,
  Loader2,
  RefreshCw,
  Video,
  XCircle,
} from "lucide-react";
import { motion } from "motion/react";
import type {
  IntegrationCredentialView,
  OnlineInterviewStatus,
  OnlineInterviewView,
  OnlineInterviewWithHostLink,
} from "@eduviaos/shared";
import {
  useCancelInterview,
  useEnquiryInterviews,
  useFetchHostLink,
  useIntegrationCredentials,
  useMintInterview,
} from "@/hooks/useOnlineInterviews";

/**
 * Phase F13 FE-1 — IA-side online interview management.
 *
 * Renders inside the EnquiryDetailPage right column. Three behaviours:
 *
 *   1. **List** — every interview ever scheduled for this enquiry,
 *      most-recently-scheduled first. Cancelled / errored rows ARE
 *      shown so the IA can audit a mint failure or a rescheduled
 *      session without leaving the page.
 *   2. **Mint** — opens a dialog with topic, agenda, scheduled time,
 *      duration, timezone, and a host-credential picker (driven by
 *      the F13 BE-1b /admin/integrations/credentials endpoint). On
 *      success, the host start_url is presented exactly once with a
 *      clipboard-copy CTA — we don't persist it client-side. Future
 *      access is routed through the audit-logged `/host-link`
 *      endpoint instead.
 *   3. **Cancel** — only available for SCHEDULED rows. STARTED /
 *      ENDED / CANCELLED / ERRORED rows show a tooltip explaining
 *      why cancel is disabled.
 *
 * Privacy
 * -------
 * The host start_url is treated as a privilege-escalation primitive
 * — anyone with it can join Zoom as the meeting host. We surface it
 * (a) on the post-mint inline banner with a single copy CTA and
 * (b) via an explicit "Copy host link" button on each SCHEDULED
 * row (which routes through the BE's audit-logged endpoint). It is
 * NEVER held in TanStack cache; it's fetched fresh on each
 * intentional access so the audit trail accurately reflects who
 * looked at it and when.
 */

// ── Display helpers ──────────────────────────────────────────────────────

const STATUS_PILL: Record<OnlineInterviewStatus, StatusPillStatus> = {
  scheduled: "active",
  started: "active",
  ended: "active",
  cancelled: "inactive",
  errored: "suspended",
};

const STATUS_LABEL: Record<OnlineInterviewStatus, string> = {
  scheduled: "Scheduled",
  started: "In progress",
  ended: "Completed",
  cancelled: "Cancelled",
  errored: "Failed to schedule",
};

function formatDateTime(iso: string, timezone?: string): string {
  try {
    const dt = new Date(iso);
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: timezone || undefined,
      timeZoneName: "short",
    }).format(dt);
  } catch {
    return iso;
  }
}

async function copyToClipboard(text: string): Promise<boolean> {
  if (!navigator.clipboard) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

// ── Section root ─────────────────────────────────────────────────────────

interface Props {
  enquiryId: string;
  /**
   * Current stage key on the enquiry. Used to:
   *   * Pre-fill the mint dialog's stage_key field.
   *   * Show / hide the "Schedule interview" CTA — we only surface
   *     it on stages whose key heuristically suggests an interview
   *     stage (key contains "interview"). The BE is the source of
   *     truth: it'll 409 if the stage doesn't accept interviews,
   *     but the FE pre-filter avoids a noisy CTA on every stage.
   */
  currentStageKey: string | null;
}

export function OnlineInterviewSection({ enquiryId, currentStageKey }: Props) {
  const interviewsQ = useEnquiryInterviews(enquiryId);
  const [mintOpen, setMintOpen] = React.useState(false);
  const [mintedHostLink, setMintedHostLink] = React.useState<OnlineInterviewWithHostLink | null>(
    null,
  );

  const items = interviewsQ.data?.interviews ?? [];
  const isInterviewStage = !!currentStageKey && /interview/i.test(currentStageKey);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32, ease: [0.32, 0.72, 0.32, 1] }}
    >
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 pb-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Video className="text-primary h-4 w-4" />
              Online interviews
            </CardTitle>
            <CardDescription>
              Zoom meetings scheduled for this enquiry. Lifecycle webhooks advance the stage
              automatically when the meeting ends.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => interviewsQ.refetch()}
              aria-label="Refresh interview list"
              disabled={interviewsQ.isFetching}
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${interviewsQ.isFetching ? "animate-spin" : ""}`}
              />
            </Button>
            {isInterviewStage && (
              <Button
                type="button"
                size="sm"
                onClick={() => setMintOpen(true)}
                disabled={!enquiryId}
              >
                <CalendarPlus className="mr-1.5 h-3.5 w-3.5" />
                Schedule interview
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {mintedHostLink && (
            <PostMintHostLinkBanner
              interview={mintedHostLink}
              onDismiss={() => setMintedHostLink(null)}
            />
          )}
          {interviewsQ.isLoading ? (
            <div
              role="status"
              aria-live="polite"
              className="text-muted-foreground flex items-center gap-2 py-4 text-sm"
            >
              <Loader2 className="text-primary h-4 w-4 animate-spin" />
              <span>Loading interviews…</span>
            </div>
          ) : interviewsQ.error ? (
            <div className="text-destructive flex items-center gap-2 text-sm">
              <AlertCircle className="h-4 w-4" />
              Couldn't load interviews. Try refreshing.
            </div>
          ) : items.length === 0 ? (
            <EmptyState canSchedule={isInterviewStage} onSchedule={() => setMintOpen(true)} />
          ) : (
            <ul className="grid gap-2.5">
              {items.map((iv) => (
                <InterviewRow key={iv.id} interview={iv} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <MintInterviewDialog
        open={mintOpen}
        onOpenChange={setMintOpen}
        enquiryId={enquiryId}
        stageKey={currentStageKey ?? ""}
        onMinted={(view) => {
          setMintedHostLink(view);
          setMintOpen(false);
        }}
      />
    </motion.div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────

function EmptyState({ canSchedule, onSchedule }: { canSchedule: boolean; onSchedule: () => void }) {
  return (
    <div className="border-border/60 bg-muted/30 rounded-lg border border-dashed py-6 text-center">
      <p className="text-muted-foreground text-sm">No interviews scheduled yet.</p>
      {canSchedule && (
        <Button type="button" variant="outline" size="sm" className="mt-3" onClick={onSchedule}>
          <CalendarPlus className="mr-1.5 h-3.5 w-3.5" />
          Schedule first interview
        </Button>
      )}
    </div>
  );
}

function InterviewRow({ interview }: { interview: OnlineInterviewView }) {
  const cancel = useCancelInterview(interview.id);
  const fetchHostLink = useFetchHostLink();
  const [confirmCancel, setConfirmCancel] = React.useState(false);
  const [cancelReason, setCancelReason] = React.useState("");
  const [copied, setCopied] = React.useState<"join" | "host" | null>(null);

  const isTerminal =
    interview.status === "ended" ||
    interview.status === "cancelled" ||
    interview.status === "errored";
  const canCancel = interview.status === "scheduled";

  const onCopyJoin = async () => {
    if (await copyToClipboard(interview.join_url)) {
      setCopied("join");
      setTimeout(() => setCopied(null), 2000);
    }
  };

  const onCopyHostLink = async () => {
    try {
      const result = await fetchHostLink.mutateAsync(interview.id);
      const ok = await copyToClipboard(result.start_url);
      if (ok) {
        setCopied("host");
        setTimeout(() => setCopied(null), 2000);
      } else {
        toast.error("Couldn't copy the host link to the clipboard.");
      }
    } catch (err) {
      // F13 — the host start_url is the privilege-escalation primitive
      // we audit-log on every fetch; surfacing failures globally is
      // the only safe path because the row UI doesn't carry a stable
      // error slot.
      const message =
        (err as { message?: string } | null)?.message ?? "Couldn't fetch the host link.";
      toast.error(message);
    }
  };

  const onConfirmCancel = async () => {
    try {
      await cancel.mutateAsync({ reason: cancelReason.trim() || null });
      setConfirmCancel(false);
      setCancelReason("");
      toast.success(`Cancelled "${interview.topic}".`);
    } catch {
      // Inline error rendered below the textarea.
    }
  };

  return (
    <li className="border-border/60 bg-card rounded-lg border p-3.5">
      <div className="flex flex-wrap items-center gap-2">
        <Video className="text-muted-foreground h-4 w-4 flex-shrink-0" />
        <span className="text-foreground text-sm font-medium">{interview.topic}</span>
        <StatusPill status={STATUS_PILL[interview.status]} className="text-[11px]">
          {STATUS_LABEL[interview.status]}
        </StatusPill>
      </div>
      <div className="text-muted-foreground mt-1.5 grid gap-x-4 gap-y-0.5 text-xs sm:grid-cols-2">
        <span>Scheduled: {formatDateTime(interview.scheduled_for, interview.timezone)}</span>
        <span>Duration: {interview.duration_minutes} min</span>
        {interview.started_at && (
          <span>Started: {formatDateTime(interview.started_at, interview.timezone)}</span>
        )}
        {interview.ended_at && (
          <span>Ended: {formatDateTime(interview.ended_at, interview.timezone)}</span>
        )}
        {interview.cancelled_at && (
          <span>Cancelled: {formatDateTime(interview.cancelled_at, interview.timezone)}</span>
        )}
      </div>
      {interview.agenda && (
        <p className="text-muted-foreground mt-2 whitespace-pre-line text-xs">{interview.agenda}</p>
      )}
      {!confirmCancel && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {!isTerminal && (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onCopyJoin}
                aria-label="Copy candidate join link"
              >
                <Copy className="mr-1.5 h-3.5 w-3.5" />
                {copied === "join" ? "Copied!" : "Copy join link"}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onCopyHostLink}
                disabled={fetchHostLink.isPending}
                aria-label="Copy host start link"
              >
                {fetchHostLink.isPending ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                )}
                {copied === "host" ? "Copied!" : "Copy host link"}
              </Button>
            </>
          )}
          {canCancel && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setConfirmCancel(true)}
              className="text-destructive hover:text-destructive"
            >
              <XCircle className="mr-1.5 h-3.5 w-3.5" />
              Cancel
            </Button>
          )}
          {interview.status === "errored" && interview.cancel_reason === null && (
            <Badge variant="outline" className="border-destructive/40 text-destructive text-[11px]">
              Mint failed — see audit log
            </Badge>
          )}
        </div>
      )}
      {confirmCancel && (
        <div className="border-border/60 bg-muted/30 mt-3 rounded-md border p-3">
          <Label htmlFor={`cancel-reason-${interview.id}`} className="text-xs">
            Cancel reason (optional, surfaces on candidate notification)
          </Label>
          <Textarea
            id={`cancel-reason-${interview.id}`}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="e.g. Counsellor unavailable — rescheduling for next week"
            rows={2}
            className="mt-1.5"
            maxLength={500}
            aria-describedby={`cancel-help-${interview.id}`}
          />
          <p id={`cancel-help-${interview.id}`} className="text-muted-foreground mt-1 text-[11px]">
            The candidate will receive an interview_cancelled email if you've configured a
            notification trigger for it.
          </p>
          {cancel.error && (
            <p className="text-destructive mt-2 text-xs">
              Couldn't cancel — try again or refresh the list.
            </p>
          )}
          <div className="mt-3 flex gap-2">
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={onConfirmCancel}
              disabled={cancel.isPending}
            >
              {cancel.isPending ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <XCircle className="mr-1.5 h-3.5 w-3.5" />
              )}
              Confirm cancel
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setConfirmCancel(false);
                setCancelReason("");
              }}
              disabled={cancel.isPending}
            >
              Keep interview
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}

function PostMintHostLinkBanner({
  interview,
  onDismiss,
}: {
  interview: OnlineInterviewWithHostLink;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = React.useState(false);
  const onCopy = async () => {
    if (await copyToClipboard(interview.start_url)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };
  return (
    <div className="border-primary/30 bg-primary/5 rounded-lg border p-3">
      <p className="text-foreground text-sm font-medium">Interview scheduled</p>
      <p className="text-muted-foreground mt-0.5 text-xs">
        The candidate has the join link via their notification. The host start link is shown once
        below — copy it to your calendar or counsellor channel. You can fetch it again at any time
        via the "Copy host link" button on the row, which is audit-logged.
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="default" onClick={onCopy}>
          <Copy className="mr-1.5 h-3.5 w-3.5" />
          {copied ? "Copied to clipboard" : "Copy host start link"}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
    </div>
  );
}

// ── Mint dialog ──────────────────────────────────────────────────────────

function MintInterviewDialog({
  open,
  onOpenChange,
  enquiryId,
  stageKey,
  onMinted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  enquiryId: string;
  stageKey: string;
  onMinted: (view: OnlineInterviewWithHostLink) => void;
}) {
  const credentialsQ = useIntegrationCredentials("zoom");
  const mint = useMintInterview(enquiryId);

  const [credentialId, setCredentialId] = React.useState<string>("");
  const [topic, setTopic] = React.useState("");
  const [agenda, setAgenda] = React.useState("");
  const [scheduledFor, setScheduledFor] = React.useState("");
  const [duration, setDuration] = React.useState("30");
  const [timezone, setTimezone] = React.useState(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
      return "UTC";
    }
  });
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setError(null);
      setTopic((prev) => prev || "Admissions interview");
      // Preselect the most recently active credential.
      const firstActive = (credentialsQ.data?.credentials ?? []).find((c) => c.status === "active");
      if (firstActive && !credentialId) setCredentialId(firstActive.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, credentialsQ.data]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!credentialId) {
      setError("Pick a connected Zoom account.");
      return;
    }
    if (!topic.trim()) {
      setError("Topic is required.");
      return;
    }
    if (!scheduledFor) {
      setError("Pick a date and time.");
      return;
    }
    const durMin = Number(duration);
    if (!Number.isFinite(durMin) || durMin < 5 || durMin > 240) {
      setError("Duration must be between 5 and 240 minutes.");
      return;
    }
    try {
      // ``datetime-local`` returns a naive local string. Convert to
      // an ISO 8601 string so the BE's tz-aware coercion is unambiguous.
      const dt = new Date(scheduledFor);
      if (Number.isNaN(dt.valueOf())) {
        setError("Couldn't parse that date — try again.");
        return;
      }
      const result = await mint.mutateAsync({
        stage_key: stageKey,
        host_credential_id: credentialId,
        topic: topic.trim(),
        agenda: agenda.trim() || null,
        scheduled_for: dt.toISOString(),
        duration_minutes: durMin,
        timezone: timezone.trim() || "UTC",
      });
      onMinted(result.interview);
      // Reset form for next open.
      setTopic("");
      setAgenda("");
      setScheduledFor("");
      setDuration("30");
    } catch (err) {
      const e = err as { message?: string; response?: { data?: { error?: { message?: string } } } };
      setError(
        e?.response?.data?.error?.message ??
          e?.message ??
          "Couldn't schedule the interview. Try again.",
      );
    }
  };

  const credentials = credentialsQ.data?.credentials ?? [];
  const activeCredentials = credentials.filter((c) => c.status === "active");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Schedule online interview</DialogTitle>
          <DialogDescription>
            Creates a Zoom meeting hosted by the chosen connected account. The candidate gets the
            join link via the configured notification trigger; you'll see the host start link
            immediately after scheduling.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="grid gap-3" noValidate>
          <div className="grid gap-1.5">
            <Label htmlFor="mint-credential">Host account</Label>
            {credentialsQ.isLoading ? (
              <p className="text-muted-foreground text-xs">Loading accounts…</p>
            ) : activeCredentials.length === 0 ? (
              <p className="text-destructive text-xs">
                No active Zoom accounts connected. Connect one from Settings → Integrations.
              </p>
            ) : (
              <Select value={credentialId} onValueChange={setCredentialId}>
                <SelectTrigger id="mint-credential">
                  <SelectValue placeholder="Pick a Zoom account" />
                </SelectTrigger>
                <SelectContent>
                  {activeCredentials.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {credentialLabel(c)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="mint-topic">Topic</Label>
            <Input
              id="mint-topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              maxLength={200}
              required
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="mint-agenda">Agenda (optional)</Label>
            <Textarea
              id="mint-agenda"
              value={agenda}
              onChange={(e) => setAgenda(e.target.value)}
              maxLength={2000}
              rows={2}
              placeholder="What will you cover? Visible to the candidate on Zoom."
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="mint-when">Date & time</Label>
              <Input
                id="mint-when"
                type="datetime-local"
                value={scheduledFor}
                onChange={(e) => setScheduledFor(e.target.value)}
                required
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="mint-duration">Duration (min)</Label>
              <Input
                id="mint-duration"
                type="number"
                min={5}
                max={240}
                step={5}
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="mint-tz">Timezone</Label>
            <Input
              id="mint-tz"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              placeholder="e.g. Asia/Kolkata"
              required
            />
          </div>

          {error && <p className="text-destructive text-xs">{error}</p>}

          <DialogFooter className="mt-1">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={mint.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mint.isPending || activeCredentials.length === 0}>
              {mint.isPending ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <CalendarPlus className="mr-1.5 h-3.5 w-3.5" />
              )}
              Schedule
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function credentialLabel(c: IntegrationCredentialView): string {
  const base = c.account_label || c.provider_account_email || c.provider_account_id || c.id;
  return c.provider_account_email && c.provider_account_email !== base
    ? `${base} · ${c.provider_account_email}`
    : base;
}
